import { useState, useEffect } from 'react';
import type { FontOption, ColorOption, GradientOption, DensityOption } from '@/components/cv/CustomizationPanel';

const useCustomization = () => {
  const [font, setFont] = useState<FontOption>('mono');
  const [color, setColor] = useState<ColorOption>('silver');
  const [gradient, setGradient] = useState<GradientOption>('subtle');
  const [density, setDensity] = useState<DensityOption>('normal');

  // Apply font changes to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('font-inter', 'font-playfair', 'font-mono');
    root.classList.add(`font-${font}`);
  }, [font]);

  // Apply color changes via CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const colors = {
      silver: { 
        primary: '220 10% 45%', 
        primaryDark: '220 15% 65%',
        gradient1: '220 10% 45%',
        gradient2: '240 8% 55%',
        gradient1Dark: '220 15% 65%',
        gradient2Dark: '240 12% 70%'
      },
      ocean: { 
        primary: '200 60% 40%', 
        primaryDark: '200 65% 55%',
        gradient1: '200 60% 40%',
        gradient2: '180 50% 45%',
        gradient1Dark: '200 65% 55%',
        gradient2Dark: '180 55% 50%'
      },
      forest: { 
        primary: '150 45% 32%', 
        primaryDark: '150 50% 45%',
        gradient1: '145 50% 38%',
        gradient2: '115 45% 42%',
        gradient1Dark: '145 55% 48%',
        gradient2Dark: '115 50% 50%'
      },
      gold: { 
        primary: '38 60% 45%', 
        primaryDark: '38 65% 55%',
        gradient1: '38 60% 45%',
        gradient2: '45 55% 55%',
        gradient1Dark: '38 65% 55%',
        gradient2Dark: '45 60% 60%'
      },
    } as const;
    
    const isDark = root.classList.contains('dark');
    const colorConfig = colors[color as keyof typeof colors];
    
    if (!colorConfig) return; // Guard against undefined color
    
    root.style.setProperty('--primary', isDark ? colorConfig.primaryDark : colorConfig.primary);
    root.style.setProperty('--accent', isDark ? colorConfig.primaryDark : colorConfig.primary);
    root.style.setProperty('--ring', isDark ? colorConfig.primaryDark : colorConfig.primary);
    
    // Set gradient variables
    root.style.setProperty('--gradient-hue-1', isDark ? colorConfig.gradient1Dark : colorConfig.gradient1);
    root.style.setProperty('--gradient-hue-2', isDark ? colorConfig.gradient2Dark : colorConfig.gradient2);
  }, [color]);

  // Apply gradient intensity
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('gradient-subtle', 'gradient-vivid', 'gradient-minimal');
    root.classList.add(`gradient-${gradient}`);
  }, [gradient]);

  // Apply density changes
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('density-compact', 'density-normal', 'density-airy');
    root.classList.add(`density-${density}`);
  }, [density]);

  return {
    font,
    setFont,
    color,
    setColor,
    gradient,
    setGradient,
    density,
    setDensity,
  };
};

export default useCustomization;
